import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEnvelope,
    faSync,
    faTriangleExclamation,
    faUser,
    faWandMagicSparkles,
    faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";

import PageContainer from "./PageContainer";
import client from "../api/client";
import { useAppBootstrap } from "../context/AppBootstrapContext";
import { domainsApi, DomainConfig } from "../api/domains";
import ModeLifecycleActions, { isRetiredMode } from "../modes/ModeLifecycleActions";
import { internalControlsEnabled } from "../utils/permissions";
import { maybeRenderProfileInternalControls } from "./profileInternalControlsEntry";
import { navigateTo } from "../utils/browser";
import { startMailboxGoogleOAuth, type MailboxStatusItem } from "../api/mailboxes";
import {
    buildMailboxCompositeSummary,
    connectionDotClass,
    mailboxStatusPillToneClasses,
    recoverableGuidanceAlertClasses,
    syncDotClass,
} from "../utils/mailboxStatusDisplay";
import MailboxLimitBlockedCallout from "./MailboxLimitBlockedCallout";
import type { MailboxLimitDetail } from "../utils/mailboxLimitMessaging";
import {
    parseMailboxLimitFromApiError,
    proactiveMailboxLimitMessage,
    reconnectBlockedByMailboxLimit,
} from "../utils/mailboxLimitMessaging";
import { getSubscriptionSnapshot, profilePlanFootnote } from "../utils/commercialPlanMessaging";
import { messageFromAxiosErrorResponse } from "../utils/axiosErrorMessage";

function MailboxGmailWebSlotEditor({
    mailbox,
    savingDisabled,
    onSaved,
    onMessage,
}: {
    mailbox: MailboxStatusItem;
    savingDisabled: boolean;
    onSaved: () => Promise<void>;
    onMessage: (msg: string | null) => void;
}) {
    const serverVal = mailbox.gmail_web_authuser_index;
    const [draft, setDraft] = useState(serverVal === null || serverVal === undefined ? "" : String(serverVal));
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setDraft(serverVal === null || serverVal === undefined ? "" : String(serverVal));
    }, [mailbox.id, serverVal]);

    const save = async () => {
        onMessage(null);
        const t = draft.trim();
        let payload: { gmail_web_authuser_index: number | null };
        if (t === "") {
            payload = { gmail_web_authuser_index: null };
        } else {
            const n = Number(t);
            if (!Number.isInteger(n) || n < 0 || n > 7) {
                onMessage("Gmail browser slot must be empty or an integer from 0 to 7.");
                return;
            }
            payload = { gmail_web_authuser_index: n };
        }
        setBusy(true);
        try {
            await client.patch(`/mailboxes/${mailbox.id}/gmail-web-session`, payload);
            await onSaved();
            onMessage("Gmail link preference saved.");
        } catch (err: any) {
            const msg = await messageFromAxiosErrorResponse(
                err?.response,
                "Could not save Gmail link preference.",
            );
            onMessage(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-3 w-full max-w-md border-t border-theme/40 pt-3">
            <div className="text-[11px] font-semibold text-theme-secondary">Open in Gmail (browser)</div>
            <p className="text-[10px] text-theme-muted mt-1 leading-snug">
                If reply links open the wrong Google account, set the slot index from your Gmail URL{" "}
                <span className="font-mono">
                    mail.google.com/mail/u/<strong>N</strong>/…
                </span>{" "}
                (0–7), or leave blank to match by mailbox email.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <label className="text-[11px] text-theme-secondary flex items-center gap-2">
                    Slot{" "}
                    <input
                        type="number"
                        min={0}
                        max={7}
                        step={1}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        className="w-16 px-2 py-1 rounded-md bg-theme-tertiary border border-theme text-theme text-xs"
                        aria-label={`Gmail browser slot for ${mailbox.email_address}`}
                    />
                </label>
                <button
                    type="button"
                    onClick={() => void save()}
                    disabled={busy || savingDisabled}
                    className="px-3 py-1 rounded-lg border border-emerald-500/30 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-500/10 disabled:opacity-50"
                >
                    {busy ? "Saving…" : "Save"}
                </button>
            </div>
        </div>
    );
}

const Profile: React.FC = () => {
    const { bootstrap, refresh, updateProfile, setActiveDomain } = useAppBootstrap();

    const [plan, setPlan] = useState<string>("free");
    const [effectivePlan, setEffectivePlan] = useState<string>("free");
    const [trialExpiresAt, setTrialExpiresAt] = useState<string | null>(null);
    const [profileSaving, setProfileSaving] = useState(false);
    const [profileMessage, setProfileMessage] = useState<string | null>(null);

    const [displayName, setDisplayName] = useState("");
    const [timezone, setTimezone] = useState("");
    const [zipCode, setZipCode] = useState("");

    const [domains, setDomains] = useState<DomainConfig[]>([]);
    const [domainsLoadError, setDomainsLoadError] = useState<string | null>(null);
    const [modeLifecycleMessage, setModeLifecycleMessage] = useState<string | null>(null);
    const [modeLifecycleMessageTone, setModeLifecycleMessageTone] = useState<"success" | "error">("error");

    const [mailboxes, setMailboxes] = useState<MailboxStatusItem[]>([]);
    const [maxMailboxes, setMaxMailboxes] = useState<number | null>(1);
    const [mailboxesLoading, setMailboxesLoading] = useState(false);
    const [gmailMessage, setGmailMessage] = useState<string | null>(null);
    const [mailboxLimitFromApi, setMailboxLimitFromApi] = useState<MailboxLimitDetail | null>(null);
    const [mailboxActionMailboxId, setMailboxActionMailboxId] = useState<number | null>(null);
    const [addMailboxEmail, setAddMailboxEmail] = useState("");

    const [proFeatureMessage, setProFeatureMessage] = useState<string | null>(null);

    const activeDomainKey = bootstrap?.active_domain_key || bootstrap?.user?.active_domain_key || "job_hunt";
    const role = bootstrap?.user?.role || null;
    const showInternalControls = internalControlsEnabled(role);

    const userEmail = bootstrap?.user?.email || "";
    const userDisplayName = bootstrap?.user?.display_name || userEmail.split("@")[0] || "User";
    const userInitial = (userDisplayName || userEmail || "?").slice(0, 1).toUpperCase();

    const timezoneOptions = useMemo(() => {
        const supported = (Intl as any).supportedValuesOf?.("timeZone");
        if (Array.isArray(supported) && supported.length > 0) return supported as string[];
        return [
            "UTC",
            "America/Los_Angeles",
            "America/Denver",
            "America/Chicago",
            "America/New_York",
            "Europe/London",
            "Europe/Paris",
            "Asia/Tokyo",
            "Asia/Seoul",
            "Asia/Singapore",
            "Australia/Sydney",
        ];
    }, []);

    const connectedMailboxes = mailboxes.filter((mailbox) => mailbox.status === "CONNECTED");
    const connectedMailboxCount = connectedMailboxes.length;
    const canAddAnotherMailbox = maxMailboxes === null || connectedMailboxCount < maxMailboxes;
    const showAddAnotherMailboxControl = connectedMailboxCount > 0;
    const addAnotherMailboxDisabled = showAddAnotherMailboxControl && !canAddAnotherMailbox;
    const mailboxSummary = maxMailboxes === null
        ? `${connectedMailboxCount} connected mailbox${connectedMailboxCount === 1 ? "" : "es"} on your current plan`
        : `${connectedMailboxCount} of ${maxMailboxes} connected mailbox${maxMailboxes === 1 ? "" : "es"}`;
    const subscriptionSnapshot = getSubscriptionSnapshot(bootstrap);
    const planStatusFootnote = profilePlanFootnote(plan, effectivePlan, trialExpiresAt, subscriptionSnapshot);

    const loadAccountPlan = async () => {
        try {
            const response = await client.get("/user/me");
            setPlan((response.data?.plan || "free").toLowerCase());
            setEffectivePlan((response.data?.effective_plan || "free").toLowerCase());
            setTrialExpiresAt(response.data?.trial_expires_at || null);
        } catch {
            setPlan("free");
            setEffectivePlan("free");
            setTrialExpiresAt(null);
        }
    };

    const loadMailboxes = async () => {
        setMailboxesLoading(true);
        try {
            const response = await client.get("/mailboxes");
            setMailboxes(Array.isArray(response.data?.mailboxes) ? response.data.mailboxes : []);
            setMaxMailboxes(response.data?.max_mailboxes !== undefined ? response.data.max_mailboxes : 1);
        } catch {
            setMailboxes([]);
            setMaxMailboxes(1);
        } finally {
            setMailboxesLoading(false);
        }
    };

    const loadDomains = async () => {
        try {
            const nextDomains = await domainsApi.getDomains({ includeRetired: true });
            setDomains(nextDomains);
            setDomainsLoadError(null);
        } catch {
            setDomainsLoadError("Could not load modes. Refresh the page or try again.");
        }
    };

    useEffect(() => {
        loadAccountPlan();
    }, []);

    useEffect(() => {
        if (!bootstrap?.user) return;
        setDisplayName(bootstrap.user.display_name || "");
        setTimezone(bootstrap.user.timezone || "");
        setZipCode(bootstrap.user.zip_code || "");
    }, [bootstrap]);

    useEffect(() => {
        loadMailboxes();
        loadDomains();
    }, []);

    const saveProfile = async () => {
        setProfileSaving(true);
        setProfileMessage(null);
        try {
            await updateProfile({
                display_name: displayName.trim(),
                timezone: timezone.trim() || undefined,
                zip_code: zipCode.trim(),
            });
            setProfileMessage("Profile saved");
        } catch {
            setProfileMessage("Failed to save profile");
        } finally {
            setProfileSaving(false);
        }
    };

    const activeDomains = domains.filter((domain) => !isRetiredMode(domain));
    const retiredDomains = domains.filter((domain) => isRetiredMode(domain));

    const handleModeLifecycleSuccess = async (
        domain: DomainConfig,
        action: "Archive" | "Deactivate" | "Activate",
        context: { wasActiveSelection: boolean },
    ) => {
        if (action === "Activate") {
            setModeLifecycleMessageTone("success");
            setModeLifecycleMessage(`${domain.label} is active again.`);
            return;
        }

        if (context.wasActiveSelection) {
            const activeModes = await domainsApi.getDomains();
            const fallback = activeModes.find((entry) => entry.is_system_default) ?? activeModes[0];
            const fallbackLabel = fallback?.label ?? "your default mode";
            setModeLifecycleMessageTone("success");
            setModeLifecycleMessage(
                `${domain.label} ${action === "Archive" ? "archived" : "deactivated"}. Switched your active mode to ${fallbackLabel}.`,
            );
            return;
        }

        setModeLifecycleMessageTone("success");
        setModeLifecycleMessage(
            `${domain.label} ${action === "Archive" ? "archived" : "deactivated"}.`,
        );
    };

    const refreshModes = async () => {
        setModeLifecycleMessage(null);
        await loadDomains();
        await refresh();
    };

    const connectGmail = async () => {
        setGmailMessage(null);
        setMailboxLimitFromApi(null);
        if (addAnotherMailboxDisabled) {
            return;
        }
        const addAnotherHint = addMailboxEmail.trim();
        if (showAddAnotherMailboxControl) {
            const at = addAnotherHint.indexOf("@");
            if (at <= 0 || !addAnotherHint.includes(".", at + 1)) {
                setGmailMessage("Enter the Gmail address you want to connect, then continue.");
                return;
            }
        }
        try {
            const response = await startMailboxGoogleOAuth(
                showAddAnotherMailboxControl
                    ? { connectMode: "add", emailAddress: addAnotherHint }
                    : { connectMode: "add" },
            );
            const url = response.url;
            if (url) {
                navigateTo(url);
                return;
            }
            setGmailMessage("Could not start Gmail authorization.");
        } catch (error: any) {
            const limitDetail = parseMailboxLimitFromApiError(error);
            if (limitDetail) {
                setMailboxLimitFromApi(limitDetail);
                return;
            }
            const detail = error?.response?.data?.detail;
            const asString =
                typeof detail === "string"
                    ? detail
                    : detail && typeof detail === "object" && typeof (detail as { message?: string }).message === "string"
                      ? (detail as { message: string }).message
                      : "Failed to start Gmail authorization.";
            setGmailMessage(asString);
        }
    };

    /**
     * Targeted reconnect for an existing (revoked/error) mailbox row. Sends the
     * email hint so OAuth reuses this row instead of adding a new account.
     * Disconnected reconnects still count against the plan mailbox limit.
     */
    const reconnectMailbox = async (mailbox: MailboxStatusItem) => {
        setGmailMessage(null);
        setMailboxLimitFromApi(null);
        if (reconnectBlockedByMailboxLimit(mailbox, canAddAnotherMailbox)) {
            return;
        }
        setMailboxActionMailboxId(mailbox.id);
        try {
            const response = await startMailboxGoogleOAuth({
                connectMode: "reconnect",
                emailAddress: mailbox.email_address,
            });
            const url = response.url;
            if (url) {
                navigateTo(url);
                return;
            }
            setGmailMessage("Could not start Gmail reconnection.");
        } catch (error: any) {
            const limitDetail = parseMailboxLimitFromApiError(error);
            if (limitDetail) {
                setMailboxLimitFromApi(limitDetail);
                return;
            }
            const detail = error?.response?.data?.detail;
            const asString =
                typeof detail === "string"
                    ? detail
                    : detail && typeof detail === "object" && typeof (detail as { message?: string }).message === "string"
                      ? (detail as { message: string }).message
                      : "Failed to start Gmail reconnection.";
            setGmailMessage(asString);
        } finally {
            setMailboxActionMailboxId(null);
        }
    };

    const setPrimaryMailbox = async (mailboxId: number) => {
        setGmailMessage(null);
        setMailboxActionMailboxId(mailboxId);
        try {
            await client.post(`/mailboxes/${mailboxId}/set-primary`);
            await loadMailboxes();
            await refresh();
            setGmailMessage("Primary mailbox updated.");
        } catch {
            setGmailMessage("Failed to set primary mailbox.");
        } finally {
            setMailboxActionMailboxId(null);
        }
    };

    const syncMailboxNow = async (mailboxId: number) => {
        setGmailMessage(null);
        setMailboxActionMailboxId(mailboxId);
        try {
            const res = await client.post(`/mailboxes/${mailboxId}/sync`);
            await loadMailboxes();
            await refresh();
            if (res.data?.status === "already_in_progress") {
                setGmailMessage("This mailbox is already syncing.");
            } else {
                setGmailMessage("Sync started for this mailbox.");
            }
        } catch {
            setGmailMessage("Failed to start sync for this mailbox.");
        } finally {
            setMailboxActionMailboxId(null);
        }
    };

    const disconnectMailbox = async (mailboxId: number, emailAddress: string) => {
        const ok = window.confirm(
            `Disconnect ${emailAddress}? Other connected mailboxes stay linked.`
        );
        if (!ok) return;
        setGmailMessage(null);
        setMailboxActionMailboxId(mailboxId);
        try {
            await client.post(`/mailboxes/${mailboxId}/disconnect`);
            await loadMailboxes();
            await refresh();
            setGmailMessage("Mailbox disconnected.");
        } catch {
            setGmailMessage("Failed to disconnect mailbox.");
        } finally {
            setMailboxActionMailboxId(null);
        }
    };

    const testProFeature = async () => {
        setProFeatureMessage(null);
        try {
            const response = await client.get("/pro/ping");
            setProFeatureMessage(response?.data?.status === "ok" ? "Pro feature access verified." : "Unexpected pro response.");
        } catch (error: any) {
            const detail = error?.response?.data?.detail || "Pro feature check failed.";
            setProFeatureMessage(detail);
        }
    };

    return (
        <PageContainer>
            <div className="space-y-6 fade-in">
                <div>
                    <h1 className="text-3xl font-bold text-theme tracking-tight">Profile</h1>
                    <p className="text-theme-secondary mt-1">Manage your account identity, preferences, and integrations.</p>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <div className="space-y-6">
                        <div className="glass-card p-6 flex flex-col items-center text-center bg-theme-secondary border border-theme">
                            <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-indigo-500 to-sky-500 flex items-center justify-center text-3xl font-bold text-white mb-4 shadow-lg shadow-indigo-500/20">
                                {userInitial}
                            </div>
                            <h3 className="text-xl font-bold text-theme">{userDisplayName}</h3>
                            <p className="text-sm text-theme-secondary">{userEmail || "—"}</p>
                            <div className="mt-3 px-3 py-1 bg-theme-tertiary rounded-full text-xs font-mono text-indigo-400 uppercase tracking-widest border border-indigo-500/20">
                                {effectivePlan.toUpperCase()} PLAN
                            </div>
                            <div className="mt-2 text-[11px] text-theme-muted leading-snug">{planStatusFootnote}</div>
                            {effectivePlan === "pro" && (
                                <button
                                    onClick={testProFeature}
                                    className="mt-3 px-3 py-1 rounded-lg border border-emerald-500/40 text-emerald-300 text-[11px] font-semibold hover:bg-emerald-500/10"
                                >
                                    Test PRO Access
                                </button>
                            )}
                            {proFeatureMessage && (
                                <div className="mt-2 text-[11px] text-theme-muted">
                                    {proFeatureMessage}
                                </div>
                            )}
                            {role && (
                                <div className="mt-2 text-[11px] text-theme-muted uppercase tracking-wider">
                                    Role: {role}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="xl:col-span-2 space-y-6">
                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-indigo-500 shadow-sm">
                            <h3 className="text-lg font-bold text-theme mb-2 flex items-center gap-2">
                                <FontAwesomeIcon icon={faUser} className="text-indigo-400" />
                                Identity
                            </h3>
                            <p className="text-sm text-theme-secondary mb-4">
                                Keep your account details up to date for better context and personalization.
                            </p>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <label className="text-sm text-theme-secondary">
                                    Display name
                                    <input
                                        value={displayName}
                                        onChange={(event) => setDisplayName(event.target.value)}
                                        className="mt-2 w-full px-4 py-3 rounded-lg bg-theme-tertiary border border-theme text-theme outline-none focus:border-indigo-500/50"
                                        placeholder="Your name"
                                    />
                                </label>
                                <label className="text-sm text-theme-secondary">
                                    Timezone
                                    <input
                                        value={timezone}
                                        onChange={(event) => setTimezone(event.target.value)}
                                        list="profile-timezone-options"
                                        className="mt-2 w-full px-4 py-3 rounded-lg bg-theme-tertiary border border-theme text-theme outline-none focus:border-indigo-500/50"
                                        placeholder="America/Los_Angeles"
                                    />
                                </label>
                                <label className="text-sm text-theme-secondary">
                                    Zip code
                                    <input
                                        value={zipCode}
                                        onChange={(event) => setZipCode(event.target.value)}
                                        inputMode="numeric"
                                        autoComplete="postal-code"
                                        className="mt-2 w-full px-4 py-3 rounded-lg bg-theme-tertiary border border-theme text-theme outline-none focus:border-indigo-500/50"
                                        placeholder="97035"
                                    />
                                </label>
                            </div>
                            <datalist id="profile-timezone-options">
                                {timezoneOptions.map((tz) => (
                                    <option key={tz} value={tz} />
                                ))}
                            </datalist>
                            <p className="text-xs text-theme-muted mt-2">
                                Today and Workbench use this timezone for due dates and day boundaries when set; otherwise your browser&apos;s zone is used.
                            </p>

                            <div className="flex items-center justify-between mt-4">
                                <div className="text-xs text-theme-muted">{profileMessage || " "}</div>
                                <button
                                    onClick={saveProfile}
                                    disabled={profileSaving || displayName.trim().length === 0}
                                    className={`px-5 py-3 rounded-lg border font-bold transition-all ${profileSaving
                                        ? "bg-theme-tertiary border-theme text-theme-muted cursor-not-allowed opacity-60"
                                        : "bg-indigo-500 hover:bg-indigo-600 border-transparent text-white shadow-lg shadow-indigo-500/20"
                                        }`}
                                >
                                    {profileSaving ? "Saving..." : "Save Profile"}
                                </button>
                            </div>
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-purple-500 shadow-sm">
                            <h3 className="text-lg font-bold text-theme mb-2 flex items-center gap-2">
                                <FontAwesomeIcon icon={faWandMagicSparkles} className="text-purple-400" />
                                Modes
                            </h3>
                            <p className="text-sm text-theme-secondary mb-4">
                                Pick your active focus mode or create your own mode.
                            </p>

                            <div className="flex flex-col gap-3">
                                {activeDomains.map((domain) => {
                                    const selected = activeDomainKey === domain.key;
                                    return (
                                        <div key={domain.key} className="flex flex-wrap items-center gap-2">
                                            <button
                                                onClick={() => setActiveDomain(domain.key)}
                                                className={`px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider border transition-all ${selected
                                                    ? "bg-purple-500/20 border-purple-500/40 text-purple-300 shadow-lg"
                                                    : "border-theme text-theme-secondary hover:text-theme hover:bg-theme-card-hover"
                                                    }`}
                                                style={{ borderColor: selected ? undefined : "var(--color-border)" }}
                                                title={domain.description || domain.label}
                                            >
                                                {(domain.icon || "").trim()} {domain.label}
                                            </button>
                                            <ModeLifecycleActions
                                                mode={domain}
                                                activeDomainKey={activeDomainKey}
                                                onChanged={refreshModes}
                                                onError={(message) => {
                                                    setModeLifecycleMessageTone("error");
                                                    setModeLifecycleMessage(message);
                                                }}
                                                onSuccess={(action, context) => {
                                                    void handleModeLifecycleSuccess(domain, action, context);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            {retiredDomains.length > 0 && (
                                <details className="mt-4 rounded-lg border border-theme bg-theme-tertiary/30 px-3 py-2">
                                    <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-theme-secondary">
                                        Archived & inactive
                                    </summary>
                                    <div className="mt-3 flex flex-col gap-2">
                                        {retiredDomains.map((domain) => {
                                            const status = domain.lifecycle_status === "archived" ? "archived" : "inactive";
                                            return (
                                                <div key={domain.key} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-theme bg-theme-secondary px-3 py-2">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-semibold text-theme">
                                                            {(domain.icon || "").trim()} {domain.label}
                                                        </div>
                                                        <div className="text-[11px] uppercase tracking-wider text-theme-muted">
                                                            {status}
                                                        </div>
                                                    </div>
                                                    <ModeLifecycleActions
                                                        mode={domain}
                                                        activeDomainKey={activeDomainKey}
                                                        onChanged={refreshModes}
                                                        onError={(message) => {
                                                            setModeLifecycleMessageTone("error");
                                                            setModeLifecycleMessage(message);
                                                        }}
                                                        onSuccess={(action, context) => {
                                                            void handleModeLifecycleSuccess(domain, action, context);
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                </details>
                            )}

                            {domainsLoadError && (
                                <div className="mt-3 text-xs text-red-300" role="alert">
                                    {domainsLoadError}
                                </div>
                            )}

                            {modeLifecycleMessage && (
                                <div
                                    className={`mt-3 text-xs ${
                                        modeLifecycleMessageTone === "success" ? "text-emerald-300" : "text-red-300"
                                    }`}
                                    role="status"
                                >
                                    {modeLifecycleMessage}
                                </div>
                            )}

                            {activeDomains.length === 0 && (
                                <div className="text-xs text-theme-muted">
                                    No active custom modes are available.
                                </div>
                            )}

                            <div className="flex flex-wrap items-center justify-between gap-3 mt-6">
                                <p className="text-xs text-theme-muted">
                                    Need fields, statuses, or classification rules? Use the guided builder.
                                </p>
                                <Link
                                    to="/app/modes/new?returnTo=/app/profile"
                                    className="px-5 py-3 rounded-lg bg-purple-500 hover:bg-purple-600 border-transparent text-white font-bold shadow-lg shadow-purple-500/20 transition-all"
                                >
                                    Create mode
                                </Link>
                            </div>
                        </div>

                        <div className="glass-card border-none bg-theme-secondary p-6 border-l-4 border-l-emerald-500 shadow-sm space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <h3 className="text-lg font-bold text-theme flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-emerald-400" />
                                        Gmail Integrations
                                    </h3>
                                    <p className="text-xs text-theme-secondary mt-1">
                                        {mailboxSummary}
                                    </p>
                                </div>
                                <button
                                    onClick={connectGmail}
                                    disabled={mailboxesLoading || addAnotherMailboxDisabled}
                                    className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {showAddAnotherMailboxControl ? "Add another Gmail" : "Connect Gmail"}
                                </button>
                            </div>

                            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100 flex items-center gap-2">
                                <FontAwesomeIcon icon={faShieldHalved} className="text-emerald-300" />
                                Read-only for job-application emails; revoke anytime.
                            </div>

                            {showAddAnotherMailboxControl && canAddAnotherMailbox && (
                                <div className="rounded-lg border border-theme bg-theme-tertiary/40 px-3 py-2 text-xs text-theme-secondary space-y-2">
                                    <p>Enter the Gmail address to connect. Google will ask you to choose that account so the mailbox already linked stays untouched.</p>
                                    <label className="block">
                                        <span className="sr-only">Gmail address to connect</span>
                                        <input
                                            type="email"
                                            value={addMailboxEmail}
                                            onChange={(event) => setAddMailboxEmail(event.target.value)}
                                            placeholder="Gmail address to connect"
                                            autoComplete="email"
                                            className="w-full bg-theme text-theme border border-theme rounded-lg px-3 py-2 text-sm"
                                        />
                                    </label>
                                </div>
                            )}

                            {addAnotherMailboxDisabled && maxMailboxes != null && (
                                <MailboxLimitBlockedCallout
                                    body={proactiveMailboxLimitMessage(maxMailboxes, connectedMailboxCount)}
                                    usageLine={`${connectedMailboxCount} of ${maxMailboxes} mailboxes connected on your plan`}
                                />
                            )}

                            {mailboxLimitFromApi && (
                                <MailboxLimitBlockedCallout
                                    body={mailboxLimitFromApi.message}
                                    detail={mailboxLimitFromApi}
                                    usageLine={
                                        mailboxLimitFromApi.limit != null && mailboxLimitFromApi.connected != null
                                            ? `${mailboxLimitFromApi.connected} of ${mailboxLimitFromApi.limit} mailboxes connected on your plan`
                                            : null
                                    }
                                />
                            )}

                            {gmailMessage && (
                                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 flex items-center gap-2">
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    {gmailMessage}
                                </div>
                            )}

                            {mailboxesLoading ? (
                                <div className="text-sm text-theme-secondary">Loading mailboxes...</div>
                            ) : mailboxes.length === 0 ? (
                                <div className="text-sm text-theme-secondary">No mailbox connected yet.</div>
                            ) : (
                                <div className="space-y-2">
                                    {mailboxes.map((mailbox) => {
                                        const composite = buildMailboxCompositeSummary(mailbox);
                                        const connPill = mailboxStatusPillToneClasses(composite.connection.tone);
                                        const syncPill = mailboxStatusPillToneClasses(composite.syncHealth.tone);
                                        const connDot = connectionDotClass(composite.connection.tone);
                                        const syncDot = syncDotClass(composite.syncHealth.tone);
                                        return (
                                            <div
                                                key={mailbox.id}
                                                className="rounded-xl border border-theme p-3 bg-theme-tertiary/60 flex flex-wrap items-center justify-between gap-3"
                                            >
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm text-theme font-semibold truncate">{mailbox.email_address}</div>
                                                    <div
                                                        className="flex flex-wrap items-center gap-2 mt-2"
                                                        role="group"
                                                        aria-label="Mailbox connection and sync status"
                                                    >
                                                        <span
                                                            title={composite.connection.ariaLabel}
                                                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${connPill}`}
                                                        >
                                                            <span className={`h-1.5 w-1.5 rounded-full ${connDot}`} aria-hidden />
                                                            Connection: {composite.connection.label}
                                                        </span>
                                                        <span
                                                            title={composite.syncHealth.ariaLabel}
                                                            className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${syncPill}`}
                                                        >
                                                            <span className={`h-1.5 w-1.5 rounded-full ${syncDot}`} aria-hidden />
                                                            Sync: {composite.syncHealth.label}
                                                        </span>
                                                        {mailbox.is_primary && mailbox.status === "CONNECTED" && (
                                                            <span className="text-[11px] text-emerald-400 font-semibold">Primary</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-theme-secondary flex flex-wrap items-center gap-2 mt-2">
                                                        <span className="flex items-center gap-1">
                                                            <FontAwesomeIcon icon={faSync} className="text-[10px]" aria-hidden />
                                                            <span>
                                                                Last successful sync: {composite.lastSuccessfulSyncLabel}
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {composite.guidance && (
                                                        <div
                                                            role="alert"
                                                            className={`mt-2 rounded-lg border px-3 py-2 text-xs ${recoverableGuidanceAlertClasses(composite.guidance.tone)}`}
                                                        >
                                                            <div className="font-semibold">{composite.guidance.headline}</div>
                                                            <div className="mt-0.5 opacity-95">{composite.guidance.nextStep}</div>
                                                        </div>
                                                    )}
                                                    {composite.syncErrorDetail && (
                                                        <div className="text-[11px] text-red-300/90 mt-2 max-w-xl break-words">
                                                            {composite.syncErrorDetail}
                                                        </div>
                                                    )}
                                                    {mailbox.provider === "gmail" && mailbox.status === "CONNECTED" && (
                                                        <MailboxGmailWebSlotEditor
                                                            mailbox={mailbox}
                                                            savingDisabled={mailboxActionMailboxId === mailbox.id}
                                                            onSaved={loadMailboxes}
                                                            onMessage={setGmailMessage}
                                                        />
                                                    )}
                                                </div>
                                                {(mailbox.status !== "CONNECTED" || mailbox.error_class === "oauth_expired") && (
                                                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                        {reconnectBlockedByMailboxLimit(mailbox, canAddAnotherMailbox) ? (
                                                            <Link
                                                                to="/app/billing"
                                                                aria-label={`Upgrade to reconnect ${mailbox.email_address}`}
                                                                className="px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold"
                                                            >
                                                                Upgrade to reconnect
                                                                <span className="sr-only"> {mailbox.email_address}</span>
                                                            </Link>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => reconnectMailbox(mailbox)}
                                                                disabled={mailboxActionMailboxId === mailbox.id}
                                                                aria-label={`Reconnect ${mailbox.email_address}`}
                                                                className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold disabled:cursor-not-allowed disabled:opacity-60"
                                                            >
                                                                Reconnect
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {mailbox.status === "CONNECTED" && (
                                                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                        {!mailbox.is_primary && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setPrimaryMailbox(mailbox.id)}
                                                                disabled={mailboxActionMailboxId === mailbox.id}
                                                                className="px-3 py-2 rounded-lg border border-amber-500/30 text-amber-300 text-xs font-semibold hover:bg-amber-500/10 disabled:opacity-50"
                                                            >
                                                                Set primary
                                                            </button>
                                                        )}
                                                        {mailbox.error_class !== "oauth_expired" && (
                                                        <button
                                                            type="button"
                                                            onClick={() => syncMailboxNow(mailbox.id)}
                                                            disabled={
                                                                mailbox.sync_in_progress ||
                                                                mailboxActionMailboxId === mailbox.id
                                                            }
                                                            className="px-3 py-2 rounded-lg border border-emerald-500/30 text-emerald-300 text-xs font-semibold hover:bg-emerald-500/10 disabled:opacity-50 inline-flex items-center gap-1.5"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faSync}
                                                                className={`text-[10px] ${mailbox.sync_in_progress ? "animate-spin" : ""}`}
                                                                aria-hidden
                                                            />
                                                            Sync now
                                                        </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => disconnectMailbox(mailbox.id, mailbox.email_address)}
                                                            disabled={
                                                                mailbox.sync_in_progress ||
                                                                mailboxActionMailboxId === mailbox.id
                                                            }
                                                            className="px-3 py-2 rounded-lg border border-red-500/30 text-red-400 text-xs font-semibold hover:bg-red-500/10 disabled:opacity-50"
                                                        >
                                                            Disconnect
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {showInternalControls &&
                            maybeRenderProfileInternalControls({
                                bootstrap,
                                refresh,
                                loadAccountPlan,
                            })}
                    </div>
                </div>
            </div>
        </PageContainer>
    );
};

export default Profile;
